import {
  Banknote,
  CreditCard,
  FileCheck2,
  Hourglass,
  type LucideIcon,
  PackageSearch,
  ShoppingBag,
  Truck,
  Video,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { FEATURE_EPIC, type FeatureFlag } from "./flags";
import { useFeature } from "./useFeatureFlags";

const ICON: Readonly<Record<FeatureFlag, LucideIcon>> = {
  "payments.stripe": CreditCard,
  "payments.stripe_connect": Banknote,
  "channels.ebay": ShoppingBag,
  "channels.whatnot": Video,
  "channels.mercari": PackageSearch,
  "channels.tiktok_shop": Video,
  "tax.nta_api": FileCheck2,
  "shipping.shipandco": Truck,
  "shipping.zonos": Truck,
};

export interface ComingSoonCardProps {
  feature: FeatureFlag;
  /** Override the epic recorded in `data-epic` ("T20–T22") — never shown to staff (U-13). */
  epic?: string;
  /** `compact` = one line inside a menu / dialog. */
  variant?: "card" | "compact";
  className?: string;
}

/**
 * A phase-gated feature (R18): icon + one line + "can be switched on later, when the owner
 * decides" — the epic (T2x) is a developer word, kept in `data-epic` only (U-13, T13-29 review).
 * Disabled, no button, no call to the platform. Renders nothing when the flag is on (the real
 * screen of T20–T23 takes the place).
 */
export function ComingSoonCard({
  feature,
  epic,
  variant = "card",
  className,
}: ComingSoonCardProps) {
  const { t } = useTranslation();
  const enabled = useFeature(feature);
  if (enabled) {
    return null;
  }
  const Icon = ICON[feature];
  const key = feature.replace(".", "_");
  const title = t(`flags.${key}.title`);
  const epicId = epic ?? FEATURE_EPIC[feature];
  const when = t("flags.when");
  if (variant === "compact") {
    return (
      <p
        data-slot="coming-soon"
        data-feature={feature}
        data-epic={epicId}
        aria-disabled="true"
        className={cn(
          "flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground",
          className,
        )}
      >
        <Hourglass className="size-4 shrink-0" aria-hidden />
        <span>
          {title} — {t("nav.coming_soon")}
        </span>
      </p>
    );
  }
  return (
    <section
      data-slot="coming-soon"
      data-feature={feature}
      data-epic={epicId}
      aria-disabled="true"
      aria-label={`${title} — ${t("nav.coming_soon")}`}
      className={cn(
        "flex items-start gap-3 rounded-lg border border-dashed bg-muted/40 p-4",
        className,
      )}
    >
      <Icon className="mt-0.5 size-6 shrink-0 text-muted-foreground" aria-hidden />
      <div className="flex min-w-0 flex-col gap-1">
        <h3 className="flex flex-wrap items-center gap-2 font-semibold">
          {title}
          <span className="rounded-full border px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {t("nav.coming_soon")}
          </span>
        </h3>
        <p className="text-sm text-muted-foreground">{t(`flags.${key}.body`)}</p>
        <p className="text-xs text-muted-foreground">{when}</p>
      </div>
    </section>
  );
}
